import React, { useState } from 'react';

/**
 * ImageThumbnail Component
 *
 * Displays an image thumbnail with:
 * - Loading state with skeleton/spinner
 * - Proper aspect ratio handling
 * - Click to enlarge functionality
 * - Error state handling
 */
function ImageThumbnail({
  src,
  alt,
  onClick,
  className = '',
  maxWidth = 200,
  maxHeight = 150,
  showOverlay = true
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div
      className={`relative group inline-block ${className}`}
      style={{ maxWidth, maxHeight }}
    >
      {/* Loading skeleton */}
      {isLoading && (
        <div
          className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse flex items-center justify-center"
          style={{ width: maxWidth * 0.75, height: maxHeight * 0.75 }}
        >
          <svg
            className="w-8 h-8 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div
          className="flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          style={{ width: maxWidth * 0.75, height: maxHeight * 0.75 }}
        >
          <svg
            className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Failed to load
          </span>
        </div>
      )}

      {/* Image */}
      <img
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        onClick={onClick}
        className={`
          rounded-lg object-cover transition-all duration-200
          ${isLoading || hasError ? 'opacity-0 absolute' : 'opacity-100'}
          ${onClick ? 'cursor-pointer hover:opacity-90' : ''}
        `}
        style={{
          maxWidth,
          maxHeight,
        }}
        draggable={false}
      />

      {/* Zoom overlay on hover */}
      {showOverlay && onClick && !isLoading && !hasError && (
        <div
          className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors flex items-center justify-center cursor-pointer"
          onClick={onClick}
        >
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-2">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageThumbnail;
